import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Icon from 'components/priority/Icon/Icon'
import { useUpscalePriceLabel } from '@/hooks/caseImages/useUpscalePriceLabel'
import * as S from './CaseImageTile.styled'
import { CASE_IMAGES_TEST_IDS } from './caseImagesTestIds'

type LowResolutionBadgeProps = {
  /** Called when the user chooses to enhance from inside the tooltip. */
  onEnhance: () => void
}

/**
 * Warning icon on a small photo. Its tooltip is a child of the same anchor,
 * so CSS `:hover` on the anchor keeps it open while the pointer travels
 * from the icon to the "Enhance with AI" button; `:focus-within` does the
 * same for the keyboard, and a click pins it open. The price comes from
 * the pricing atom and is left out of the copy until it has loaded.
 */
export const LowResolutionBadge = ({ onEnhance }: LowResolutionBadgeProps) => {
  const { t } = useTranslation()
  const price = useUpscalePriceLabel()
  const [pinned, setPinned] = useState(false)

  const copy = price
    ? t('caseImages.tile.lowResolution', { price })
    : t('caseImages.tile.lowResolutionNoPrice')

  return (
    <S.LowResAnchor>
      <S.TileIconButton
        type="button"
        aria-label={copy}
        aria-expanded={pinned}
        data-testid={CASE_IMAGES_TEST_IDS.LOW_RES_BADGE}
        onClick={(event) => {
          event.stopPropagation()
          setPinned((current) => !current)
        }}
      >
        <Icon
          icon="exclamationTriangle"
          fill="orange"
          width="18px"
          height="18px"
        />
      </S.TileIconButton>
      <S.LowResTooltip
        role="tooltip"
        $pinned={pinned}
        data-testid={CASE_IMAGES_TEST_IDS.LOW_RES_TOOLTIP}
      >
        {copy}
        <div>
          <S.LowResTooltipButton
            type="button"
            data-testid={CASE_IMAGES_TEST_IDS.LOW_RES_ENHANCE}
            onClick={(event) => {
              event.stopPropagation()
              setPinned(false)
              onEnhance()
            }}
          >
            {t('caseImages.tile.lowResolutionEnhance')}
          </S.LowResTooltipButton>
        </div>
      </S.LowResTooltip>
    </S.LowResAnchor>
  )
}
